import { Suspense, useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { Kbd, ModKey } from '@/components/ui/kbd'
import { Button } from '@/components/ui/button'
import AppErrorBoundary from '@/components/common/AppErrorBoundary'
import { assistAnchor } from '@/features/assist/anchors'
import { useHasOpened } from '@/hooks/useHasOpened'
import { lazyWithRetry } from '@/utils/lazyWithRetry'

const CommandPaletteDialog = lazyWithRetry(() => import('@/components/CommandPaletteDialog'), 'components/CommandPaletteDialog')

/**
 * CommandPalette - the topbar search field and its Cmd/Ctrl+K shortcut.
 *
 * The search dialog (`CommandPaletteDialog`) is fetched the first time the
 * palette opens and stays mounted after that, so later opens are instant.
 *
 * @component
 */
export default function CommandPalette() {
  const [isOpen, setIsOpen] = useState(false)
  const hasOpened = useHasOpened(isOpen)

  // Handle keyboard shortcut (Cmd/Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      {/* Trigger Button - the topbar search field, on the control radius/height. */}
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        {...assistAnchor('nav.search')}
        className="@container/command h-10 w-full max-w-md min-w-0 justify-start gap-2 px-3 font-normal text-ink-2"
        aria-label="Open command palette"
      >
        <Search className="size-4 shrink-0" aria-hidden="true" />
        <span className="hidden min-w-0 flex-1 truncate text-left @[5rem]/command:block"><span className="@[14rem]/command:hidden">Search…</span><span className="hidden @[14rem]/command:inline">Search your workspace…</span></span>
        <Kbd className="hidden @[20rem]/command:inline-flex">
          <ModKey />
          K
        </Kbd>
      </Button>

      {hasOpened && (
        <AppErrorBoundary name="search" floating>
          <Suspense fallback={null}>
            <CommandPaletteDialog isOpen={isOpen} setIsOpen={setIsOpen} />
          </Suspense>
        </AppErrorBoundary>
      )}
    </>
  )
}
